import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Settings.css';
import ConfirmDialog from '../confirmDialog';
import i18n from '../../i18n/config';

const Settings = ({ onBack, user, onLogout, onDeleteAccount }) => {
  const { t } = useTranslation();
  const [cacheSize, setCacheSize] = useState('0 MB');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'zh');

  // 监听语言变化
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setCurrentLanguage(lng);
    };
    
    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  // Calculate cache size
  useEffect(() => {
    const calculateCacheSize = async () => {
      let totalSize = 0;
      
      // Calculate localStorage size
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          totalSize += localStorage[key].length + key.length;
        }
      }
      
      // Calculate sessionStorage size
      for (let key in sessionStorage) {
        if (sessionStorage.hasOwnProperty(key)) {
          totalSize += sessionStorage[key].length + key.length;
        }
      }
      
      // Calculate cache size if available
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          for (const cacheName of cacheNames) {
            const cache = await caches.open(cacheName);
            const keys = await cache.keys();
            for (const request of keys) {
              const response = await cache.match(request);
              if (response) {
                const blob = await response.blob();
                totalSize += blob.size;
              }
            }
          }
        } catch (error) {
          console.error('Error calculating cache size:', error);
        }
      }
      
      // Format size
      const sizeInMB = (totalSize / (1024 * 1024)).toFixed(1);
      setCacheSize(`${sizeInMB} MB`);
    };
    
    calculateCacheSize();
  }, []);

  const handleClearCache = () => {
    // Clear cache and force refresh H5
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    // Clear localStorage and sessionStorage
    localStorage.clear();
    sessionStorage.clear();
    // Force reload page
    window.location.reload(true);
  };

  const handlePrivacyPolicy = () => {
    // Open privacy policy page
    window.open('/privacy-policy.html', '_blank');
  };

  const handleDeleteAccountClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (onDeleteAccount) {
      onDeleteAccount();
    }
    setShowDeleteConfirm(false);
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    // Return to chat page after logout
    if (onBack) {
      onBack();
    }
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
    localStorage.setItem('app_language', lang);
  };

  // Settings sections
  const settingsSections = [
    {
      title: t('settings.storageManagement'),
      items: [
        {
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="3" y1="9" x2="21" y2="9"></line>
              <line x1="9" y1="21" x2="9" y2="9"></line>
            </svg>
          ),
          title: t('settings.cacheSize'),
          description: t('settings.cacheSizeDescription'),
          action: (
            <div className="settings-item-action-group">
              <span className="settings-cache-size">{cacheSize}</span>
              <button className="settings-action-btn settings-action-btn-primary" onClick={handleClearCache}>
                {t('settings.clear')}
              </button>
            </div>
          )
        }
      ]
    },
    {
      title: t('settings.helpSupport'),
      items: [
        {
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              <path d="M9 12l2 2 4-4"></path>
            </svg>
          ),
          title: t('settings.privacyPolicy'),
          description: t('settings.privacyPolicyDescription'),
          action: (
            <button className="settings-action-btn" onClick={handlePrivacyPolicy}>
              {t('settings.view')}
            </button>
          )
        },
        {
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6m0 4h.01"></path>
            </svg>
          ),
          title: t('settings.language'),
          description: t('settings.languageDescription'),
          action: (
            <div className="settings-item-action-group">
              <button 
                className={`settings-language-btn ${currentLanguage === 'zh' ? 'settings-language-btn-active' : ''}`}
                onClick={() => handleLanguageChange('zh')}
              >
                {t('settings.chinese')}
              </button>
              <button 
                className={`settings-language-btn ${currentLanguage === 'en' ? 'settings-language-btn-active' : ''}`}
                onClick={() => handleLanguageChange('en')}
              >
                {t('settings.english')}
              </button>
            </div>
          )
        }
      ]
    },
    {
      title: t('settings.dangerousOperations'),
      items: [
        {
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18"></path>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          ),
          title: t('settings.deleteAccount'),
          description: t('settings.deleteAccountDescription'),
          action: (
            <button className="settings-action-btn settings-action-btn-danger" onClick={handleDeleteAccountClick}>
              {t('settings.delete')}
            </button>
          ),
          show: user
        },
        {
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          ),
          title: t('settings.logout'),
          description: t('settings.logoutDescription'),
          action: (
            <button className="settings-action-btn settings-action-btn-danger" onClick={handleLogout}>
              {t('settings.logout')}
            </button>
          ),
          show: user
        }
      ]
    }
  ];

  return (
    <div className="settings-container">
      {/* Header */}
      <div className="settings-header">
        <button 
          className="settings-back-btn" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onBack) {
              onBack();
            }
          }}
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <h1 className="settings-title">{t('settings.title')}</h1>
      </div>

      {/* Content */}
      <div className="settings-content">
        {settingsSections.map((section, sectionIndex) => {
          // Filter items that should be shown
          const visibleItems = section.items.filter(item => {
            // If item has a show property, use it
            if (item.show !== undefined) {
              return item.show;
            }
            // Otherwise, show by default
            return true;
          });

          // If no items to show, skip the entire section
          if (visibleItems.length === 0) {
            return null;
          }

          return (
            <div key={sectionIndex} className="settings-section">
              <h2 className="settings-section-title">{section.title}</h2>
              {visibleItems.map((item, itemIndex) => (
                <div key={itemIndex} className="settings-item">
                  <div className="settings-item-icon">
                    {item.icon}
                  </div>
                  <div className="settings-item-content">
                    <h3 className="settings-item-title">{item.title}</h3>
                    <p className="settings-item-description">{item.description}</p>
                  </div>
                  <div className="settings-item-action">
                    {item.action}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDialog.deleteAccountTitle')}
        message={t('confirmDialog.deleteAccountMessage')}
        confirmText={t('confirmDialog.deleteAccountConfirm')}
        cancelText={t('confirmDialog.cancel')}
        type="danger"
      />
    </div>
  );
};

export default Settings;

